import React, { useEffect, useMemo, useState } from 'react'
import { X } from 'lucide-react'
import { apiClient } from '../services/apiClient'

interface ProjectSettingsDialogProps {
  isOpen: boolean
  onClose: () => void
  projectId: string
}

type ProposalType = 'milestones' | 'sla'

interface ProjectResponse {
  id: string
  name: string
  industry: string | null
  proposal_type: string | null
  llm_model_id: string | null
}

interface PromptTemplate {
  id: string
  scope: 'system' | 'user'
  owner_user_id: string | null
  name: string
  agent_name: string
  industry: string | null
  proposal_type: string | null
  content: string
  created_at: string
  updated_at: string
}

interface PromptSelectionResponse {
  agent_name: string
  prompt_id: string | null
  selected_by_user_id: string | null
  updated_at: string
}

export function ProjectSettingsDialog({ isOpen, onClose, projectId }: ProjectSettingsDialogProps) {
  const agentOptions = useMemo(
    () => [
      'project_intake',
      'solution_architecture',
      'scope_decomposition',
      'effort_estimation',
      'proposal_generation',
      'success_assessment',
      'proposal_review',
    ],
    []
  )

  const proposalTypeOptions = useMemo(
    () => [
      { value: 'milestones' as const, label: 'Milestones (Phased)' },
      { value: 'sla' as const, label: 'SLA (Service Levels)' },
    ],
    []
  )

  const modelOptions = useMemo(
    () => [
      { value: 'us.amazon.nova-pro-v1:0', label: 'Amazon Nova Pro' },
      { value: 'arn:aws:bedrock:us-east-2:949701862374:inference-profile/global.anthropic.claude-sonnet-4-5-20250929-v1:0', label: 'Claude 4.5 Sonnet' },
      { value: 'arn:aws:bedrock:us-east-2:949701862374:inference-profile/global.anthropic.claude-opus-4-5-20251101-v1:0', label: 'Claude 4.5 Opus' },
    ],
    []
  )

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const [projectName, setProjectName] = useState('')
  const [proposalType, setProposalType] = useState<ProposalType>('milestones')
  const [llmModelId, setLlmModelId] = useState(modelOptions[0].value)

  const [promptsByAgent, setPromptsByAgent] = useState<Record<string, PromptTemplate[]>>({})
  const [selectedPromptIdByAgent, setSelectedPromptIdByAgent] = useState<Record<string, string | null>>({})
  const [isSavingProject, setIsSavingProject] = useState(false)
  const [savingAgent, setSavingAgent] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return

    const load = async () => {
      setIsLoading(true)
      setError(null)
      setNotice(null)

      try {
        const proj = await apiClient.get<ProjectResponse>(`/api/projects/${encodeURIComponent(projectId)}`)
        setProjectName(proj.name)
        setProposalType((proj.proposal_type as ProposalType) || 'milestones')
        setLlmModelId(proj.llm_model_id || modelOptions[0].value)

        const selections = await apiClient.get<PromptSelectionResponse[]>(
          `/api/prompts/projects/${encodeURIComponent(projectId)}/selections`
        )

        const selected: Record<string, string | null> = {}
        for (const a of agentOptions) selected[a] = null
        for (const s of selections) {
          selected[s.agent_name] = s.prompt_id
        }
        setSelectedPromptIdByAgent(selected)

        const promptsResults = await Promise.all(
          agentOptions.map(async (agent) => {
            const list = await apiClient.get<PromptTemplate[]>(
              `/api/prompts/system?agent_name=${encodeURIComponent(agent)}`
            )
            return [agent, list] as const
          })
        )

        const next: Record<string, PromptTemplate[]> = {}
        for (const [agent, list] of promptsResults) {
          next[agent] = list
        }
        setPromptsByAgent(next)
      } catch (e: any) {
        setError(e?.message || 'Failed to load project settings')
      } finally {
        setIsLoading(false)
      }
    }

    void load()
  }, [isOpen, projectId, agentOptions, modelOptions])

  const saveProject = async () => {
    setIsSavingProject(true)
    setError(null)
    setNotice(null)

    try {
      await apiClient.patch<ProjectResponse>(`/api/projects/${encodeURIComponent(projectId)}`, {
        proposal_type: proposalType,
        llm_model_id: llmModelId,
      })
      setNotice('Project settings saved')
    } catch (e: any) {
      setError(e?.message || 'Failed to save project settings')
    } finally {
      setIsSavingProject(false)
    }
  }

  const saveAgentSelection = async (agentName: string, promptId: string | null) => {
    setSavingAgent(agentName)
    setError(null)
    setNotice(null)

    try {
      await apiClient.request<PromptSelectionResponse>(
        `/api/prompts/projects/${encodeURIComponent(projectId)}/selections/${encodeURIComponent(agentName)}`,
        {
          method: 'PUT',
          body: JSON.stringify({ prompt_id: promptId }),
        }
      )

      setSelectedPromptIdByAgent((prev) => ({ ...prev, [agentName]: promptId }))
      setNotice('Prompt selection saved')
    } catch (e: any) {
      setError(e?.message || 'Failed to save prompt selection')
    } finally {
      setSavingAgent(null)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <style>{`
        .dialog-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(4px);
        }

        .dialog-content {
          position: relative;
          background: var(--warm-white);
          border-radius: 12px;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          max-width: 720px;
          width: 100%;
          margin: 1rem;
          max-height: 90vh;
          overflow-y: auto;
        }

        .dialog-input {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 1.5px solid var(--sand);
          border-radius: 8px;
          background: var(--warm-white);
          font-family: 'DM Sans', sans-serif;
          font-size: 0.9375rem;
          color: var(--charcoal);
          transition: all 0.2s;
          outline: none;
        }

        .dialog-input:focus {
          border-color: var(--terracotta);
          box-shadow: 0 0 0 3px rgba(200, 88, 53, 0.1);
        }

        .dialog-button {
          padding: 0.75rem 1.25rem;
          border-radius: 8px;
          font-family: 'DM Sans', sans-serif;
          font-size: 0.9375rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .dialog-button-primary {
          background: var(--terracotta);
          color: white;
          border: none;
        }

        .dialog-button-secondary {
          background: transparent;
          color: var(--charcoal-light);
          border: 1.5px solid var(--sand);
        }

        .dialog-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>

      <div className="dialog-overlay" onClick={onClose} />

      <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2
                style={{
                  fontFamily: 'Crimson Text, Georgia, serif',
                  fontSize: '1.5rem',
                  fontWeight: 600,
                  color: 'var(--charcoal)',
                }}
              >
                Project Settings
              </h2>
              <div className="text-sm" style={{ color: 'var(--charcoal-light)' }}>
                {projectName ? projectName : projectId}
              </div>
            </div>

            <button onClick={onClose} className="p-1 rounded-md transition-colors" style={{ color: 'var(--charcoal-light)' }}>
              <X className="w-5 h-5" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-md" style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', color: '#991B1B', fontSize: '0.875rem' }}>
              {error}
            </div>
          )}

          {notice && (
            <div className="mb-4 p-3 rounded-md" style={{ background: 'rgba(34, 197, 94, 0.08)', border: '1px solid rgba(34, 197, 94, 0.25)', color: '#15803d', fontSize: '0.875rem' }}>
              {notice}
            </div>
          )}

          <div className="rounded-md border p-4 mb-6" style={{ borderColor: 'var(--sand)', background: 'var(--warm-white)' }}>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
              <div className="md:col-span-6">
                <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--charcoal)' }}>
                  Proposal type
                </label>
                <select className="dialog-input" value={proposalType} onChange={(e) => setProposalType(e.target.value as ProposalType)}>
                  {proposalTypeOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-6">
                <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--charcoal)' }}>
                  Model
                </label>
                <select className="dialog-input" value={llmModelId} onChange={(e) => setLlmModelId(e.target.value)}>
                  {modelOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-12 flex justify-end">
                <button onClick={saveProject} className="dialog-button dialog-button-primary" disabled={isSavingProject || isLoading}>
                  {isSavingProject ? 'Saving…' : 'Save project settings'}
                </button>
              </div>
            </div>
          </div>

          <div className="rounded-md border p-4" style={{ borderColor: 'var(--sand)', background: 'var(--warm-white)' }}>
            <div className="text-sm font-semibold mb-1" style={{ color: 'var(--charcoal)' }}>
              Agent prompts
            </div>
            <div className="text-sm mb-4" style={{ color: 'var(--charcoal-light)' }}>
              Choose a prompt per agent, or leave as Auto to use the best matching system prompt.
            </div>

            <div className="space-y-3">
              {agentOptions.map((agent) => {
                const prompts = promptsByAgent[agent] || []
                const current = selectedPromptIdByAgent[agent] ?? null

                return (
                  <div key={agent} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                    <div className="md:col-span-4">
                      <div className="text-sm font-medium" style={{ color: 'var(--charcoal)' }}>
                        {agent}
                      </div>
                    </div>

                    <div className="md:col-span-6">
                      <select
                        className="dialog-input"
                        value={current || ''}
                        onChange={(e) => {
                          const next = e.target.value ? e.target.value : null
                          setSelectedPromptIdByAgent((prev) => ({ ...prev, [agent]: next }))
                        }}
                        disabled={isLoading}
                      >
                        <option value="">Auto</option>
                        {prompts.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="md:col-span-2 flex justify-end">
                      <button
                        onClick={() => saveAgentSelection(agent, selectedPromptIdByAgent[agent] ?? null)}
                        className="dialog-button dialog-button-secondary"
                        disabled={isLoading || savingAgent === agent}
                      >
                        {savingAgent === agent ? 'Saving…' : 'Save'}
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>

            <div className="mt-4 text-xs" style={{ color: 'var(--charcoal-light)' }}>
              Tip: To force Auto again, select Auto and click Save.
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-6">
            <button onClick={onClose} className="dialog-button dialog-button-secondary">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
